import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createProject, deleteProject, listAllProjects, updateProject } from '../api/client';
import { ProjectInfo } from '../api/types';
import { Badge } from './components/badge';
import { Button } from './components/button';
import { Card, CardContent } from './components/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/table';
import ProjectForm, { ProjectFormValues } from './ProjectForm';

function ProjectsIdare() {
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectInfo | null>(null);

  const load = () => {
    setLoading(true);
    listAllProjects()
      .then(setProjects)
      .catch((err) => setError(err instanceof Error ? err.message : 'Yüklenemedi'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleEdit = (p: ProjectInfo) => {
    setEditing(p);
    setFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bu projeyi silmek istediğinize emin misiniz?')) return;
    await deleteProject(id);
    load();
  };

  const handleSubmit = async (values: ProjectFormValues) => {
    if (editing) {
      await updateProject(editing.id, values);
    } else {
      await createProject(values);
    }
    load();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Projeler</h1>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
            Portfolyo sayfasında görünen projeleri yönetin.
          </p>
        </div>
        <Button onClick={handleCreate}><Plus className="h-4 w-4" /> Yeni Proje</Button>
      </div>

      {loading && <p className="text-sm text-[hsl(var(--muted-foreground))]">Yükleniyor...</p>}
      {error && <p className="text-sm text-[hsl(var(--destructive))]">{error}</p>}

      {!loading && !error && (
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Başlık</TableHead>
                  <TableHead>Bağlantı</TableHead>
                  <TableHead>Sıra</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.title_en}</TableCell>
                    <TableCell className="max-w-[220px] truncate text-[hsl(var(--muted-foreground))]">
                      {p.link_url}
                    </TableCell>
                    <TableCell>{p.sort_order}</TableCell>
                    <TableCell>
                      <Badge variant={p.is_active ? 'default' : 'muted'}>
                        {p.is_active ? 'Aktif' : 'Pasif'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(p)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <ProjectForm open={formOpen} onOpenChange={setFormOpen} initial={editing} onSubmit={handleSubmit} />
    </div>
  );
}

export default ProjectsIdare;
